/**
 * Action plans (Phase 4B): each item with its owner, dates, progress, status and the controls and risks it
 * covers. The firm's IT lead edits them in place as priorities change; everyone else reads them. Red appears
 * only when a committed plan is off track.
 */
import { DELIVERY_STATUS } from '@grc/shared/vocabulary';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, useApiClient } from '../api';
import { Roadmap } from '../charts';
import { formatDate, Status } from '../components';
import type { ActionPlan } from '../types';

const PRIORITY: Record<string, string> = { high: 'High', medium: 'Medium', low: 'Low' };

export function PlanRoadmap({ plans }: { plans: ActionPlan[] }) {
  return (
    <Roadmap
      caption="Action plan roadmap"
      items={plans.map((p) => ({
        id: p.id,
        code: p.code,
        label: p.objective,
        start: p.startDate?.slice(0, 10) ?? null,
        target: p.targetDate?.slice(0, 10) ?? null,
        progress: p.progress,
        delivery: p.deliveryStatus,
        deliveryLabel: DELIVERY_STATUS[p.deliveryStatus]?.label ?? p.deliveryStatus,
      }))}
    />
  );
}

export function ActionPlanList({ firmCode, plans, canUpdate, risks, onChanged }: {
  firmCode: string;
  plans: ActionPlan[];
  canUpdate: boolean;
  risks?: Array<{ id: number; code: string; title: string }>;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  return (
    <>
      {plans.length === 0 && <p className="muted">No action plans yet.</p>}
      <ul className="plain plans-list">
        {plans.map((p) => (
          <PlanItem key={p.id} firmCode={firmCode} plan={p} canUpdate={canUpdate} risks={risks} onChanged={onChanged} />
        ))}
      </ul>
      {canUpdate &&
        (adding ? (
          <NewPlan firmCode={firmCode} onDone={() => (setAdding(false), onChanged())} onCancel={() => setAdding(false)} />
        ) : (
          <button className="pill secondary-dark" onClick={() => setAdding(true)}>
            Add an action plan
          </button>
        ))}
    </>
  );
}

function PlanItem({ firmCode, plan: p, canUpdate, risks, onChanged }: {
  firmCode: string;
  plan: ActionPlan;
  canUpdate: boolean;
  risks?: Array<{ id: number; code: string; title: string }>;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const riskCodes = p.risks.map((id) => risks?.find((r) => r.id === id)?.code).filter(Boolean);
  return (
    <li className="plan">
      <div className="plan-head">
        <span className="plan-code">{p.code}</span>
        <Status map={DELIVERY_STATUS} value={p.deliveryStatus} />
        {p.priority && <span className="muted small">{PRIORITY[p.priority]} priority</span>}
      </div>
      <p className="plan-objective">{p.objective}</p>
      <div className="plan-meta small">
        <span>Owner: {p.owner ?? 'not confirmed'}</span>
        <span>Target: {p.targetDate ? formatDate(p.targetDate) : p.targetNote ?? 'not set'}</span>
        <span>{p.progress ?? 0}% done</span>
      </div>
      <div className="progress" role="img" aria-label={`${p.progress ?? 0}% done`}>
        <span style={{ width: `${p.progress ?? 0}%` }} />
      </div>
      {(p.controls.length > 0 || riskCodes.length > 0) && (
        <div className="tags">
          {p.controls.map((c) => (
            <Link key={c} className="tag control-tag" to={`/firm/${firmCode}/controls/${c}`}>
              {c}
            </Link>
          ))}
          {riskCodes.map((c) => (
            <span key={c} className="tag risk-tag">
              {c}
            </span>
          ))}
        </div>
      )}
      {p.updatedBy && <p className="muted small">Last updated by {p.updatedBy}, {formatDate(p.updatedAt)}</p>}
      {canUpdate &&
        (editing ? (
          <EditPlan firmCode={firmCode} plan={p} risks={risks} onDone={() => (setEditing(false), onChanged())} onCancel={() => setEditing(false)} />
        ) : (
          <button className="link" onClick={() => setEditing(true)} aria-label={`Update ${p.code}`}>
            Update
          </button>
        ))}
    </li>
  );
}

function EditPlan({ firmCode, plan: p, risks, onDone, onCancel }: {
  firmCode: string;
  plan: ActionPlan;
  risks?: Array<{ id: number; code: string; title: string }>;
  onDone: () => void;
  onCancel: () => void;
}) {
  const call = useApiClient();
  const [f, setF] = useState({
    objective: p.objective,
    owner: p.owner ?? '',
    priority: p.priority ?? 'medium',
    startDate: p.startDate?.slice(0, 10) ?? '',
    targetDate: p.targetDate?.slice(0, 10) ?? '',
    progress: p.progress ?? 0,
    deliveryStatus: p.deliveryStatus,
    controls: p.controls.join(', '),
    risks: p.risks,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await call(`/firms/${firmCode}/action-plans/${p.id}`, {
        method: 'PATCH',
        body: {
          objective: f.objective,
          owner: f.owner || null,
          priority: f.priority,
          startDate: f.startDate || null,
          targetDate: f.targetDate || null,
          progress: Number(f.progress),
          deliveryStatus: f.deliveryStatus,
          status: f.deliveryStatus === 'complete' ? 'complete' : Number(f.progress) > 0 ? 'in_progress' : 'not_started',
          controls: f.controls.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean),
          ...(risks ? { risks: f.risks.map(String) } : {}),
        },
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That change did not save.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="form plan-form" onSubmit={save} aria-label={`Update ${p.code}`}>
      <label>
        Objective
        <textarea rows={2} required maxLength={4000} value={f.objective} onChange={(e) => setF({ ...f, objective: e.target.value })} />
      </label>
      <div className="row">
        <label>
          Owner
          <input maxLength={200} value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} />
        </label>
        <label>
          Priority
          <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value as 'high' })}>
            {Object.entries(PRIORITY).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="row">
        <label>
          Start
          <input type="date" value={f.startDate} onChange={(e) => setF({ ...f, startDate: e.target.value })} />
        </label>
        <label>
          Target
          <input type="date" value={f.targetDate} onChange={(e) => setF({ ...f, targetDate: e.target.value })} />
        </label>
      </div>
      <div className="row">
        <label>
          Progress: {f.progress}%
          <input type="range" min={0} max={100} step={5} value={f.progress} onChange={(e) => setF({ ...f, progress: Number(e.target.value) })} />
        </label>
        <label>
          Status
          <select value={f.deliveryStatus} onChange={(e) => setF({ ...f, deliveryStatus: e.target.value })}>
            {Object.entries(DELIVERY_STATUS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label>
        Controls covered <span className="muted small">(numbers, separated by commas, for example 5.3, 6.2)</span>
        <input value={f.controls} onChange={(e) => setF({ ...f, controls: e.target.value })} />
      </label>
      {risks && risks.length > 0 && (
        <fieldset className="attach">
          <legend className="small">Risks this plan addresses</legend>
          {risks.map((r) => (
            <label key={r.id} className="check">
              <input type="checkbox" checked={f.risks.includes(r.id)} onChange={(e) => setF({ ...f, risks: e.target.checked ? [...f.risks, r.id] : f.risks.filter((x) => x !== r.id) })} />
              {r.code} {r.title}
            </label>
          ))}
        </fieldset>
      )}
      <div className="actions">
        <button className="pill" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="pill secondary-dark" onClick={onCancel}>
          Cancel
        </button>
      </div>
      {error && (
        <p role="alert" className="notice">
          {error}
        </p>
      )}
    </form>
  );
}

export function NewPlan({ firmCode, onDone, onCancel, riskId }: { firmCode: string; onDone: () => void; onCancel: () => void; riskId?: number }) {
  const call = useApiClient();
  const [f, setF] = useState({ theme: '', objective: '', owner: '', targetDate: '', controls: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await call(`/firms/${firmCode}/action-plans`, {
        method: 'POST',
        body: {
          theme: f.theme,
          objective: f.objective,
          owner: f.owner || null,
          targetDate: f.targetDate || null,
          controls: f.controls.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean),
          risks: riskId ? [String(riskId)] : [],
        },
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The plan was not saved.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="form plan-form" onSubmit={save} aria-label="New action plan">
      <div className="row">
        <label>
          Theme
          <input required minLength={3} maxLength={200} value={f.theme} onChange={(e) => setF({ ...f, theme: e.target.value })} placeholder="for example, Identity and access" />
        </label>
        <label>
          Owner
          <input maxLength={200} value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} />
        </label>
      </div>
      <label>
        Objective
        <textarea rows={2} required minLength={10} maxLength={4000} value={f.objective} onChange={(e) => setF({ ...f, objective: e.target.value })} />
      </label>
      <div className="row">
        <label>
          Target date
          <input type="date" value={f.targetDate} onChange={(e) => setF({ ...f, targetDate: e.target.value })} />
        </label>
        <label>
          Controls covered
          <input value={f.controls} onChange={(e) => setF({ ...f, controls: e.target.value })} placeholder="5.3, 6.2" />
        </label>
      </div>
      <div className="actions">
        <button className="pill" disabled={busy}>
          {busy ? 'Saving…' : 'Add plan'}
        </button>
        <button type="button" className="pill secondary-dark" onClick={onCancel}>
          Cancel
        </button>
      </div>
      {error && (
        <p role="alert" className="notice">
          {error}
        </p>
      )}
    </form>
  );
}
